import React, { useState, useEffect } from 'react';
import _ from 'lodash';
import { Button, Header, SelectMenu, BaseIcon, DatePicker, Tooltip } from '@scale/scaleui';
import { useOCPStore } from '../hooks/useOCPStore';
import FlowName from '../components/FlowName';
import { useDraftFlowStore } from '../hooks/useDraftFlowStore';
import {
  getFlowById,
  saveDraft,
  publishDraft,
  queryClient,
  fetchSignupFlow,
  deleteSignupFlowDraftForOnboardingFlow,
  SignupFlowResponse,
} from '../api';
import { toast } from 'react-toastify';
import {
  FlowValidationError,
  IOnboardingFlowDraft,
  validateFlowStructure,
  OnboardingFlowType,
  OnboardingFlowStepNode,
  getFlowSteps,
  getOnboardingStepAttributeMap,
  OnboardingStepAttributeKey,
} from '@scale/shared/onboardingControlPanel';
import PublishDraftModal from 'react-scale/components/GrowthCenter/OnboardingControlPanel/components/PublishDraftModal';
import { useBypassWhitelistStore } from 'react-scale/components/GrowthCenter/OnboardingControlPanel/hooks/useBypassWhitelistStore';
import { AddWorkerModal } from 'react-scale/components/GrowthCenter/OnboardingControlPanel/components/AddWorkerModal';
import { RemoveWorkerModal } from 'react-scale/components/GrowthCenter/OnboardingControlPanel/components/RemoveWorkerModal';
import { Link, useLocation } from 'react-router-dom';
import { Routes } from 'react-scale/components/GrowthCenter/urls';
import FilterComponent from '../../components/FilterComponent';
import SelectTag from 'react-scale/corp/components/Lookup/sections/SelectTag';
import {
  isSignupFlowConfigEmpty,
  validateSignupFlowConfig,
  signupFlowFieldsMatch,
} from 'react-scale/components/GrowthCenter/OnboardingControlPanel/components/signupFlow/SignupFlowConfigInput';
import { DuplicateFlowModal } from 'react-scale/components/GrowthCenter/OnboardingControlPanel/components/DuplicateFlowModal';

// eslint-disable-next-line complexity
const OCPHeader: React.FC = () => {
  const [savingDraft, setSavingDraft] = useState<boolean>(false);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [addWorkerModalOpen, setAddWorkerModalOpen] = useState<boolean>(false);
  const [removeWorkerModalOpen, setRemoveWorkerModalOpen] = useState<boolean>(false);
  const [duplicateFlowModalOpen, setDuplicateFlowModalOpen] = useState<boolean>(false);
  const {
    currentFlow,
    setCurrentFlow,
    currentVersion,
    setCurrentVersion,
    flowVersions,
    addFlowVersion,
    setValidationResults,
    toggleTestFunnelModal,
    setInfoBannerData,
    flowFilterTags,
    setFlowFilterTags,
    flowFilterDates,
    setFlowFilterDates,
    unsavedSignupFlow,
    setUnsavedSignupFlow,
    setManuallySetSignupFlow,
    setSelectedSignupFlowId,
    publishingDraft,
    setPublishingDraft,
    isValidSignupFlowName,
    setIsValidSignupFlowName,
    changedSignupFlowOnboardingFlow,
    setChangedSignupFlowOnboardingFlow,
  } = useOCPStore(state => ({
    currentFlow: state.currentFlow,
    setCurrentFlow: state.setCurrentFlow,
    currentVersion: state.currentVersion,
    setCurrentVersion: state.setCurrentVersion,
    flowVersions: state.flowVersions,
    addFlowVersion: state.addFlowVersion,
    setValidationResults: state.setValidationResults,
    toggleTestFunnelModal: state.toggleTestFunnelModal,
    setInfoBannerData: state.setInfoBannerData,
    flowFilterTags: state.flowFilterTags,
    setFlowFilterTags: state.setFlowFilterTags,
    flowFilterDates: state.flowFilterDates,
    setFlowFilterDates: state.setFlowFilterDates,
    unsavedSignupFlow: state.unsavedSignupFlow,
    setUnsavedSignupFlow: state.setUnsavedSignupFlow,
    setManuallySetSignupFlow: state.setManuallySetSignupFlow,
    setSelectedSignupFlowId: state.setSelectedSignupFlowId,
    publishingDraft: state.publishingDraft,
    setPublishingDraft: state.setPublishingDraft,
    isValidSignupFlowName: state.isValidSignupFlowName,
    setIsValidSignupFlowName: state.setIsValidSignupFlowName,
    changedSignupFlowOnboardingFlow: state.changedSignupFlowOnboardingFlow,
    setChangedSignupFlowOnboardingFlow: state.setChangedSignupFlowOnboardingFlow,
  }));
  const { draftFlows, replaceDraftFlow } = useDraftFlowStore(state => ({
    draftFlows: state.draftFlows,
    replaceDraftFlow: state.replaceDraftFlow,
  }));
  const { loadBypassWhitelist, publishWhitelistStore } = useBypassWhitelistStore(state => ({
    loadBypassWhitelist: state.loadBypassWhitelist,
    publishWhitelistStore: state.publishBypassWhitelist,
  }));
  const versions = [
    ...([
      ...Array(
        (Math.max(
          ...Object.keys(flowVersions[currentFlow?._id ?? ''] ?? { 0: '' }).map((n: string) =>
            Number(n),
          ),
        ) ?? 0) + 1,
      ).keys(),
    ].map(version => ({
      id: version,
      name: `v${version}`,
    })) ?? []),
    { id: -1, name: 'Draft' },
  ];
  // TODO: move saveDraft and publishDraft logic out of the header into a hook
  const handleSaveDraft = async () => {
    // TODO: run validations
    setSavingDraft(true);
    if (!currentFlow) return;
    const oldId = currentFlow._id;
    const newFlow =
      currentFlow._id.startsWith('tempId_') ||
      draftFlows[currentFlow._id ?? '']._id.startsWith('tempId_') ||
      !draftFlows[currentFlow._id ?? ''].originalFlowId.length;
    let draftFlow: any = {
      ..._.omit(draftFlows[currentFlow._id], ['_id']),
      type: OnboardingFlowType.Growth,
    };
    if (newFlow) draftFlow = _.omit(draftFlow, ['_id', 'originalFlowId']);
    if (draftFlow) {
      let flow;
      try {
        const savedDraft = await saveDraft(
          draftFlow as IOnboardingFlowDraft<string>,
          newFlow ? undefined : currentFlow._id,
          hasUnsavedSignupFlow
            ? { ...unsavedSignupFlow, onboardingFlow: currentFlow._id }
            : undefined,
        );
        if (savedDraft) {
          await publishWhitelistStore();
          replaceDraftFlow(oldId, savedDraft);
          if (savedDraft.originalFlowId) {
            flow = await getFlowById(savedDraft.originalFlowId);
            addFlowVersion(flow);
            setCurrentFlow(flow);
            setCurrentVersion(-1);
          }
          if (newFlow && hasUnsavedSignupFlow) {
            setUnsavedSignupFlow({ ...unsavedSignupFlow, onboardingFlow: flow?._id });
          }
          await loadBypassWhitelist(savedDraft);
          toast(`Successfully saved draft`, {
            type: 'success',
            autoClose: 5000,
            position: 'top-right',
          });
        }
      } catch (e) {
        toast(
          e.response?.message ? `Error saving draft: ${e.response.message}` : 'Error saving draft',
          {
            type: 'error',
            autoClose: 5000,
            position: 'top-right',
          },
        );
      }
    }
    setSavingDraft(false);
  };

  const [hasUnsavedSignupFlow, setHasUnsavedSignupFlow] = useState<boolean>(false);

  const handlePublishDraft = async () => {
    if (!currentFlow) return;
    setPublishingDraft(true);
    const oldId = currentFlow._id;
    const newFlow =
      currentFlow._id.startsWith('tempId_') ||
      draftFlows[currentFlow._id ?? '']._id.startsWith('tempId_') ||
      !draftFlows[currentFlow._id ?? ''].originalFlowId.length;
    let draftFlow: any = { ...draftFlows[currentFlow._id], type: OnboardingFlowType.Growth };
    if (newFlow) draftFlow = _.omit(draftFlow, ['_id', 'originalFlowId']);
    if (draftFlow) {
      try {
        formatDraftFlow(draftFlow);
        const { flow, draft, signupFlow } = await publishDraft(
          draftFlow as IOnboardingFlowDraft<string>,
          hasUnsavedSignupFlow ? unsavedSignupFlow : undefined,
        );
        await publishWhitelistStore();
        if (hasUnsavedSignupFlow && flow && signupFlow) {
          queryClient.invalidateQueries([flow._id, 'signupFlows']);
          queryClient.invalidateQueries([`signup-flow-info-${flow._id}`]);
          queryClient.invalidateQueries(['signupFlow', signupFlow._id]);
          setManuallySetSignupFlow(false);
          setUnsavedSignupFlow({});
          setSelectedSignupFlowId(signupFlow._id);
        }

        if (flow) {
          addFlowVersion(flow);
          if (newFlow) {
            replaceDraftFlow(oldId, draft);
            setCurrentFlow(flow);
          } else {
            setCurrentVersion(flow.version);
          }
          await loadBypassWhitelist(flow);

          toast(`Successfully published draft`, {
            type: 'success',
            autoClose: 5000,
            position: 'top-right',
          });
        }
      } catch (e) {
        toast(
          e.response?.message
            ? `Error publishing draft: ${e.response.message}`
            : 'Error publishing draft',
          {
            type: 'error',
            autoClose: 5000,
            position: 'top-right',
          },
        );
      }
    }
    setPublishingDraft(false);
    setModalOpen(false);
  };

  const formatDraftFlow = (draftFlow: IOnboardingFlowDraft<string>) => {
    const stepRequiresConfigMap = getOnboardingStepAttributeMap(
      OnboardingStepAttributeKey.RequiresConfig,
    );
    const steps: OnboardingFlowStepNode[] = getFlowSteps(draftFlow);
    for (const step of steps) {
      if (!stepRequiresConfigMap[step.stepType]) {
        step.config = undefined;
      }
    }
  };

  const validateFlow = async () => {
    if (!currentFlow) return;
    const draftFlow = {
      ...draftFlows[currentFlow._id],
      originalFlowId: currentFlow._id,
      isPublished: false,
    };
    try {
      const errors: FlowValidationError[] = validateFlowStructure(draftFlow);
      if (errors.length > 0) {
        setValidationResults({ isValid: false, errors });
        return;
      } else {
        setValidationResults({ isValid: true, errors });
      }
    } catch (e) {
      toast(`Error validating flow: ${e.message}`, {
        type: 'error',
        autoClose: 5000,
        position: 'top-right',
      });
      setValidationResults({ isValid: false, errors: [] });
      return;
    }
  };

  const handlePublishPreview = async () => {
    setModalOpen(true);
    await validateFlow();
  };

  // Misnomer, this actually resets the local (not saved) draft to the last published version
  const handleDiscardChanges = () => {
    if (!currentFlow) return;
    deleteSignupFlowDraftForOnboardingFlow(currentFlow._id);
    setManuallySetSignupFlow(false);
    setIsValidSignupFlowName(true);
    setChangedSignupFlowOnboardingFlow(false);
    setSelectedSignupFlowId('');
    setUnsavedSignupFlow({});
    const lastPublishedKey = Math.max(
      ...Object.keys(flowVersions[currentFlow._id]).map((n: string) => Number(n)),
    );
    const lastPublished = flowVersions[currentFlow._id][lastPublishedKey];
    if (lastPublished) {
      const newDraft = {
        ..._.omit(lastPublished, ['_id', 'version', 'isPublished']),
        _id: draftFlows[currentFlow._id]?._id ?? currentFlow._id,
        version: -1,
        originalFlowId: lastPublished._id,
      };
      replaceDraftFlow(currentFlow._id, newDraft);
      setCurrentFlow(lastPublished);
    }
  };

  const editMode = currentVersion === -1;

  const location = useLocation();
  // @ts-expect-error @types/react@18 $FIXME: state type is unknown
  const { podsToFilterFor } = location.state ?? {};

  useEffect(() => {
    if (publishingDraft) return;
    const checkNeedPublishSignupFlow = async () => {
      if (_.every(unsavedSignupFlow, isSignupFlowConfigEmpty)) {
        setHasUnsavedSignupFlow(false);
      } else if (!unsavedSignupFlow._id) {
        setHasUnsavedSignupFlow(true);
      } else {
        let oldSignupFlow = queryClient.getQueryData<SignupFlowResponse>([
          'signupFlow',
          unsavedSignupFlow._id,
        ]);
        if (!oldSignupFlow) {
          try {
            oldSignupFlow = await fetchSignupFlow(unsavedSignupFlow._id);
            queryClient.setQueryData(['signupFlow', unsavedSignupFlow._id], oldSignupFlow);
          } catch (e) {
            setHasUnsavedSignupFlow(true);
            return;
          }
        }
        setHasUnsavedSignupFlow(!signupFlowFieldsMatch(unsavedSignupFlow, oldSignupFlow));
      }
    };

    checkNeedPublishSignupFlow();
  }, [publishingDraft, unsavedSignupFlow]);

  return (
    <Header className="flex px-2 z-10 flex-col items-start">
      <div className="flex pb-2 pt-2 w-full">
        <div>
          <Link
            to={{
              pathname: Routes.OnboardingControlPanel,
              state: { podsToFilterFor },
            }}
          >
            <Button className="w-fit bg-transparent shadow-none" variant="neutral">
              <BaseIcon contrastText edge="start" icon={['fas', 'arrow-left']} variant="neutral" />
              Back to Funnels
            </Button>
          </Link>
        </div>
        <div className="flex ml-auto mr-2 gap-2">
          <Button variant="secondary" onClick={() => setDuplicateFlowModalOpen(true)}>
            Duplicate
          </Button>
          <DuplicateFlowModal
            isOpen={duplicateFlowModalOpen}
            onClose={() => setDuplicateFlowModalOpen(false)}
            flowName={currentFlow?.name ?? 'flow'}
            flowId={currentFlow?._id ?? ''}
          />
          <Button
            variant="secondary"
            onClick={() => {
              setAddWorkerModalOpen(true);
              setInfoBannerData(null);
            }}
          >
            Add Contributors
          </Button>
          <AddWorkerModal
            isOpen={addWorkerModalOpen}
            onClose={() => setAddWorkerModalOpen(false)}
            flowName={currentFlow?.name ?? 'flow'}
            flowId={currentFlow?._id ?? ''}
          ></AddWorkerModal>
          <Button
            variant="secondary"
            onClick={() => {
              setRemoveWorkerModalOpen(true);
              setInfoBannerData(null);
            }}
          >
            Remove Contributors
          </Button>
          <RemoveWorkerModal
            isOpen={removeWorkerModalOpen}
            onClose={() => setRemoveWorkerModalOpen(false)}
            flowName={currentFlow?.name ?? 'flow'}
            flowId={currentFlow?._id ?? ''}
          ></RemoveWorkerModal>
          <Button
            variant="secondary"
            onClick={() => {
              toggleTestFunnelModal(true);
              setInfoBannerData(null);
            }}
          >
            Test
          </Button>
          {editMode ? (
            <>
              {!currentFlow?._id?.startsWith('tempId_') && (
                <Button
                  variant="secondary"
                  disabled={savingDraft || publishingDraft}
                  onClick={handleDiscardChanges}
                >
                  Discard Changes
                </Button>
              )}
              <Tooltip
                side="bottom"
                target={
                  <div>
                    <Button
                      variant="outline"
                      disabled={
                        !isValidSignupFlowName ||
                        changedSignupFlowOnboardingFlow ||
                        savingDraft ||
                        publishingDraft
                      }
                      onClick={handleSaveDraft}
                    >
                      Save Draft
                    </Button>
                  </div>
                }
                text={
                  !isValidSignupFlowName
                    ? 'Signup flow name is not unique'
                    : changedSignupFlowOnboardingFlow
                    ? "Signup flow's onboarding flow has changed, please save signup flow before saving draft"
                    : ''
                }
              />
              <Tooltip
                side="bottom"
                target={
                  <div>
                    <Button
                      disabled={
                        (!_.every(unsavedSignupFlow, isSignupFlowConfigEmpty) &&
                          !!validateSignupFlowConfig(unsavedSignupFlow)) ||
                        !isValidSignupFlowName ||
                        changedSignupFlowOnboardingFlow ||
                        savingDraft ||
                        publishingDraft
                      }
                      onClick={handlePublishPreview}
                    >
                      Publish
                    </Button>
                  </div>
                }
                text={
                  !_.every(unsavedSignupFlow, isSignupFlowConfigEmpty) &&
                  !!validateSignupFlowConfig(unsavedSignupFlow)
                    ? 'Invalid signup flow configuration'
                    : !isValidSignupFlowName
                    ? 'Signup flow name is not unique'
                    : changedSignupFlowOnboardingFlow
                    ? "Signup flow's onboarding flow has changed, please save signup flow before publishing"
                    : ''
                }
              />
              <PublishDraftModal
                isOpen={modalOpen}
                onClose={() => setModalOpen(false)}
                onPublish={handlePublishDraft}
                hasUnsavedSignupFlow={hasUnsavedSignupFlow}
              />
            </>
          ) : (
            <Button variant="primary" onClick={() => setCurrentVersion(-1)}>
              Edit
            </Button>
          )}
        </div>
      </div>
      <div className="flex pb-2 w-full">
        <div className="flex">
          <FlowName />
          <SelectMenu
            optionClassName="cursor-pointer"
            options={versions}
            selectedOptionId={currentVersion}
            setSelected={id => setCurrentVersion(id)}
          />
        </div>
        <div className="flex ml-auto max-w-[50%]">
          <div className="max-w-[50%] mr-2">
            <FilterComponent
              filterMessage={
                flowFilterTags.length === 0
                  ? 'Filter'
                  : flowFilterTags.map(tag => tag.label).join(', ')
              }
              openRight={true}
            >
              <SelectTag
                showTagId={false}
                value={flowFilterTags}
                onChange={setFlowFilterTags}
                multi={true}
                placeholder={'Filter tags'}
              />
            </FilterComponent>
          </div>
          <div className="mr-2">
            <DatePicker
              value={flowFilterDates}
              onChange={setFlowFilterDates}
              placeholder={new Date().toString()}
              isRangePicker
            />
          </div>
        </div>
      </div>
    </Header>
  );
};

export default OCPHeader;
